import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import BugIcon from '@mui/icons-material/BugReport';
import UploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NatureIcon from '@mui/icons-material/Nature';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import CameraIcon from '@mui/icons-material/CameraAlt';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { pestDetectionService } from '../../services/pestDetectionService';
import { SelectChangeEvent } from '@mui/material/Select';
import { farmingPracticesService } from '../../services/farmingPracticesService';
// Using NatureIcon as a replacement for EcoIcon which is not available

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`farm-practices-tabpanel-${index}`}
      aria-labelledby={`farm-practices-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `farm-practices-tab-${index}`,
    'aria-controls': `farm-practices-tabpanel-${index}`,
  };
}

interface FarmInfo {
  location: string;
  cropType: string;
  soilType: string;
  farmSize: string;
  irrigationMethod: string;
}

interface Recommendation {
  title: string;
  description: string;
  icon: React.ReactNode;
  tags: string[];
}

const BestPractices: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [farmInfo, setFarmInfo] = useState<FarmInfo>({
    location: '',
    cropType: '',
    soilType: '',
    farmSize: '',
    irrigationMethod: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<Recommendation[] | null>(null);
  const [detectionResult, setDetectionResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent<string>
  ) => {
    if ('target' in e && e.target && typeof e.target.value === 'string') {
      setFarmInfo({ ...farmInfo, [e.target.name!]: e.target.value });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setDetectionResult(null);
    
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Validate file type
      if (!file.type.match('image.*')) {
        setError('Please select an image file (JPEG, PNG)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setDetectionResult(null);
  };

  const getIconForRecommendation = (title: string) => {
    const t = title.toLowerCase();
    if (t.includes('soil')) return <LocalOfferIcon color="primary" fontSize="large" />;
    if (t.includes('water') || t.includes('irrig')) return <WaterDropIcon color="primary" fontSize="large" />;
    if (t.includes('climate') || t.includes('weather')) return <ThermostatIcon color="primary" fontSize="large" />;
    if (t.includes('pest') || t.includes('disease')) return <BugIcon color="primary" fontSize="large" />;
    return <NatureIcon color="primary" fontSize="large" />;
  };

  const generateRecommendations = async () => {
    // Validate form
    if (!farmInfo.location || !farmInfo.cropType || !farmInfo.soilType) {
      setError('Please fill in the required fields (Location, Crop Type, and Soil Type)');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const recs = await farmingPracticesService.getRecommendations({
        location: farmInfo.location,
        cropType: farmInfo.cropType,
        soilType: farmInfo.soilType,
        farmSize: farmInfo.farmSize,
        irrigationMethod: farmInfo.irrigationMethod,
      });

      setRecommendations(
        recs.map((r) => ({
          title: r.title,
          description: r.description,
          icon: getIconForRecommendation(r.title),
          tags: r.tags,
        }))
      );
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch recommendations.');
    } finally {
      setLoading(false);
    }
  };

  const analyzeImage = async () => {
    if (!selectedFile) {
      setError('Please select an image to analyze');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await pestDetectionService.detectDisease(selectedFile);
      setDetectionResult(result);
    } catch (e: any) {
      console.error('Error analyzing image:', e);
      setError(e?.message || 'Failed to analyze image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText', 
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <AgricultureIcon fontSize="large" />
          <Typography variant="h4" component="h2">
            AI-assisted Best Farming Practices
          </Typography>
        </Box>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="farm practices tabs"
            variant="fullWidth"
          >
            <Tab 
              label="Personalized Recommendations" 
              icon={<NatureIcon />} 
              iconPosition="start"
              {...a11yProps(0)} 
            />
            <Tab 
              label="Pest & Disease Detection" 
              icon={<BugIcon />} 
              iconPosition="start"
              {...a11yProps(1)} 
            />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Get Personalized Farming Recommendations
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter your farm details below to receive AI-powered recommendations tailored to your specific farming conditions.
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location/County"
                name="location"
                value={farmInfo.location}
                onChange={handleInputChange}
                required
                helperText="Enter your farm's location or county"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Crop Type"
                name="cropType"
                value={farmInfo.cropType}
                onChange={handleInputChange}
                required
                helperText="What are you growing?"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel id="soil-type-label">Soil Type</InputLabel>
                <Select
                  labelId="soil-type-label"
                  name="soilType"
                  value={farmInfo.soilType}
                  label="Soil Type"
                  onChange={handleInputChange}
                >
                  <MenuItem value="Clay">Clay</MenuItem>
                  <MenuItem value="Sandy">Sandy</MenuItem>
                  <MenuItem value="Loamy">Loamy</MenuItem>
                  <MenuItem value="Silty">Silty</MenuItem>
                  <MenuItem value="Peaty">Peaty</MenuItem>
                  <MenuItem value="Chalky">Chalky</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Farm Size (acres)"
                name="farmSize"
                type="number"
                value={farmInfo.farmSize}
                onChange={handleInputChange}
                helperText="Approximate size in acres"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="irrigation-method-label">Irrigation Method</InputLabel>
                <Select
                  labelId="irrigation-method-label"
                  name="irrigationMethod"
                  value={farmInfo.irrigationMethod}
                  label="Irrigation Method"
                  onChange={handleInputChange}
                >
                  <MenuItem value="Drip">Drip Irrigation</MenuItem>
                  <MenuItem value="Sprinkler">Sprinkler System</MenuItem>
                  <MenuItem value="Flood">Flood Irrigation</MenuItem>
                  <MenuItem value="Furrow">Furrow Irrigation</MenuItem>
                  <MenuItem value="Rainfed">Rainfed (No Irrigation)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={generateRecommendations}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <NatureIcon />}
              >
                Generate Recommendations
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {recommendations && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Your Personalized Recommendations
              </Typography>
              <Grid container spacing={3}>
                {recommendations.map((rec, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card elevation={2}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ mr: 2 }}>{rec.icon}</Box>
                          <Typography variant="h6">{rec.title}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {rec.description}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {rec.tags.map((tag, i) => (
                            <Chip key={i} label={tag} size="small" color="primary" variant="outlined" />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Pest & Disease Detection
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload an image of your plant or animal to identify potential pests or diseases and get treatment recommendations.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>How our analysis works:</strong> Our system analyzes the visual characteristics of your plant image 
              to identify diseases with high accuracy. For best results, ensure the affected area is clearly visible, 
              well-lit, and in focus. The system can identify 10 different crop diseases with up to 98% accuracy.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  border: '2px dashed', 
                  borderColor: 'primary.main', 
                  p: 3, 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {!previewUrl ? (
                  <>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="upload-image-file"
                      type="file"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="upload-image-file">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<UploadIcon />}
                      >
                        Upload Image
                      </Button>
                    </label>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Or take a photo
                    </Typography>
                    <IconButton color="primary" aria-label="take photo" component="span" sx={{ mt: 1 }}>
                      <CameraIcon fontSize="large" />
                    </IconButton>
                  </>
                ) : (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} 
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRemoveImage}
                      >
                        Remove
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={analyzeImage}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <BugIcon />}
                      >
                        Analyze Image
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {detectionResult ? (
                <Card elevation={3}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        {detectionResult.disease}
                      </Typography>
                      <Chip 
                        label={`${Math.round(detectionResult.confidence * 100)}% Confidence`}
                        color="primary" 
                        size="small" 
                        sx={{ ml: 2 }} 
                      />
                    </Box>
                    
                    <Typography variant="body2" paragraph>
                      {detectionResult.description}
                    </Typography>
                    
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography><strong>Treatment Recommendations</strong></Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {detectionResult.treatment.map((item: string, i: number) => (
                            <ListItem key={i}>
                              <ListItemIcon>
                                <CheckCircleIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <Typography variant="body2">{item}</Typography>
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                    
                    <Accordion sx={{ mt: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography><strong>Prevention Measures</strong></Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {detectionResult.preventionMeasures.map((item: string, i: number) => (
                            <ListItem key={i}>
                              <ListItemIcon>
                                <InfoIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <Typography variant="body2">{item}</Typography>
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              ) : (
                <Paper elevation={0} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', bgcolor: 'background.default' }}>
                  <BugIcon color="disabled" sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body1" color="text.secondary" align="center">
                    Upload an image and click "Analyze Image" to detect pests and diseases
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
          
          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default BestPractices;
